/* eslint-disable no-async-promise-executor */
import { canvasRequest } from '$/api/canvasRquest';
import APP from '$/api/api';
import { createAgentTeamRoom, submitAgentTeamRoomOnCreate } from '$/domains/teamAgent/teamAgentService';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getCanvasListV3 } from './workFlowV3Service';
import type {
  CanvasIdentifier,
  TeamDuplicateProjectResult,
  TeamFolderCreateResult,
  TeamHomeItem,
  TeamHomeListResult,
  TeamHomeProjectRoomItem,
  TeamImportedProjectItem,
  TeamImportProjectSource,
  TeamImportProjectsResult,
  TeamProjectListResult,
  TeamSkippedImportProjectItem,
  V3CanvasListItem,
  V3CanvasListResult,
  V3CanvasProjectItem,
  V3CanvasRoomListItem,
  V3CanvasRoomListResult,
} from './type';

const TEAM_IMPORT_PROJECTS_FAILED_MESSAGE = 'import_from_personal_project_failed_please_try_again_later';

/** 单次从个人项目导入最多选择的项目数。 */
export const TEAM_IMPORT_PROJECT_LIMIT = 20;

/** 团队首页接口在当前成员席位冻结时返回的业务码。 */
export const TEAM_HOME_ACCESS_SUSPENDED_CODE = 15219;

interface TeamImportProjectPayload {
  project_id: string;
  room_id: string;
  to_room_id: string;
  name: string;
}

interface TeamDuplicateProjectRoomPayload {
  room_id: string;
  to_room_id: string;
}

/** 从团队首页接口同步抛错及 Axios 异步错误中统一识别席位冻结业务码。 */
export function isTeamHomeAccessSuspendedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const errorRecord = error as {
    code?: unknown;
    data?: { code?: unknown; };
    response?: { data?: { code?: unknown; }; };
  };
  const businessCode = errorRecord.code
    ?? errorRecord.data?.code
    ?? errorRecord.response?.data?.code;
  return Number(businessCode) === TEAM_HOME_ACCESS_SUSPENDED_CODE;
}

/**
 * 团队项目目录聚合列表（GET /team/home/list.json）。
 * 服务端已按 updated_at 排序，前端保持原顺序展示。
 */
export async function getTeamHomeList(params: {
  folder_id?: string | number;
  cursor?: string | number;
  count?: number;
} = {}): Promise<TeamHomeListResult> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamHomeList,
    method: 'GET',
    params: {
      folder_id: params.folder_id ?? 0,
      cursor: params.cursor ?? '0',
      count: params.count ?? 20,
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    const data = response.data.data || {};
    const list = ((data.list || []) as TeamHomeItem[]).map((item) => {
      if (item.type !== 'project' || item.name?.trim()) return item;
      const firstRoomName = item.rooms?.[0]?.name?.trim();
      return firstRoomName ? { ...item, name: firstRoomName } : item;
    });
    return {
      folder_id: data.folder_id ?? params.folder_id ?? 0,
      parent_folder_id: data.parent_folder_id ?? 0,
      list,
      next_cursor: data.next_cursor == null ? undefined : String(data.next_cursor),
      has_more: data.has_more === true || Number(data.has_more) === 1,
    };
  }
  const error = new Error(
    response.data?.message || useLanguageStore.getState()
      .languageStore.smartcatTranslationText((v) => v.get_team_project_list_failed),
  ) as Error & { code?: number; };
  const businessCode = Number(response.data?.code);
  if (Number.isFinite(businessCode)) error.code = businessCode;
  throw error;
}

/**
 * 新建团队项目文件夹；parent_folder_id=0 表示在团队项目首页创建。
 */
export async function createTeamProjectFolder(params: {
  name?: string;
  cover_url?: string;
  parent_folder_id?: string | number;
} = {}): Promise<TeamFolderCreateResult> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamFolderCreate,
    method: 'POST',
    data: {
      name: params.name,
      cover_url: params.cover_url,
      parent_folder_id: Number(params.parent_folder_id ?? 0),
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data;
  }
  throw new Error(response.data?.message || useLanguageStore.getState()
    .languageStore.smartcatTranslationText((v) => v.create_team_project_folder_failed));
}

/** 更新团队项目文件夹名称或封面，未传字段由服务端保持原值。 */
export async function updateTeamProjectFolder(
  folderId: string | number,
  payload: { name?: string; cover_url?: string; },
): Promise<Record<string, never>> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamFolderUpdate,
    method: 'POST',
    data: {
      folder_id: Number(folderId),
      ...payload,
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data || {};
  }
  throw new Error(response.data?.message || useLanguageStore.getState()
    .languageStore.smartcatTranslationText((v) => v.update_team_project_folder_failed));
}

/** 删除团队项目文件夹及其下级内容。 */
export async function deleteTeamProjectFolder(
  folderId: string | number,
): Promise<Record<string, never>> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamFolderDelete,
    method: 'POST',
    data: {
      folder_id: Number(folderId),
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data || {};
  }
  throw new Error(response.data?.message || useLanguageStore.getState()
    .languageStore.smartcatTranslationText((v) => v.delete_team_project_folder_failed));
}

/** 将团队项目移动到目标文件夹；folderId=0 表示移回团队项目首页。 */
export async function moveTeamProjectToFolder(
  projectId: string | number,
  folderId: string | number,
): Promise<Record<string, never>> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamFolderProjectMove,
    method: 'POST',
    data: {
      folder_id: Number(folderId),
      project_id: String(projectId),
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    return response.data.data || {};
  }
  throw new Error(response.data?.message || useLanguageStore.getState()
    .languageStore.smartcatTranslationText((v) => v.move_team_project_failed));
}

/** 按源项目的画布数量创建 Agent 目标房间，并展开为多条导入参数。 */
async function createTeamImportProjectPayloads(
  project: TeamImportProjectSource,
): Promise<TeamImportProjectPayload[]> {
  const projectId = project.project_id == null ? '' : String(project.project_id).trim();
  if (!projectId) {
    throw new Error(TEAM_IMPORT_PROJECTS_FAILED_MESSAGE);
  }
  const parsedRoomCount = Number(project.room_count);
  const roomCount = Number.isFinite(parsedRoomCount) && parsedRoomCount > 0
    ? Math.floor(parsedRoomCount)
    : 1;
  const targetRoomIds = await Promise.all(
    Array.from({ length: roomCount }, async () => {
      const agentResponse = await createAgentTeamRoom();
      const targetRoomId = agentResponse?.data?.result?.room_id as string | undefined;
      if (agentResponse?.data?.code !== 0 || !targetRoomId) {
        throw new Error(TEAM_IMPORT_PROJECTS_FAILED_MESSAGE);
      }
      return targetRoomId;
    }),
  );
  return targetRoomIds.map((targetRoomId) => ({
    project_id: projectId,
    room_id: '',
    to_room_id: targetRoomId,
    name: '',
  }));
}

/** 按项目分组创建 room_count 个 Agent 房间后，以二维数组批量复制到团队。 */
export async function importPersonalProjectsToTeam(
  projects: TeamImportProjectSource[],
  folderId?: string | number,
): Promise<TeamImportProjectsResult> {
  if (projects.length > TEAM_IMPORT_PROJECT_LIMIT) {
    throw new Error(`最多选择 ${TEAM_IMPORT_PROJECT_LIMIT} 个项目`);
  }
  const normalizedFolderId = Number(folderId ?? 0);
  const importProjectGroups = await Promise.all(projects.map(createTeamImportProjectPayloads));
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamImportProjects,
    method: 'POST',
    data: {
      projects: importProjectGroups,
      ...(normalizedFolderId > 0 ? { folder_id: normalizedFolderId } : {}),
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    return {
      count: Number(response.data.data?.count) || 0,
      imported: (response.data.data?.imported || []) as TeamImportedProjectItem[],
      skipped: (response.data.data?.skipped || []) as TeamSkippedImportProjectItem[],
    };
  }
  throw new Error(response.data?.message || TEAM_IMPORT_PROJECTS_FAILED_MESSAGE);
}

/**
 * 团队版 · 项目列表（GET /team/project/list.json）
 *
 * @export
 * @param {{ cursor?: string; count?: number | string; }} [params]
 * @return {Promise<TeamProjectListResult>}
 */
export async function getTeamProjectList(params: {
  cursor?: string;
  count?: number | string;
} = {}) {
  return new Promise<TeamProjectListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamProjectList,
      method: 'GET',
      params: {
        cursor: params.cursor,
        count: params.count,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      const data = response.data.data || {};
      resolve({
        list: (data.list || []) as V3CanvasProjectItem[],
        next_cursor: data.next_cursor,
        has_more: data.has_more,
      });
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 项目更新 / 重命名（POST /team/project/update.json）
 *
 * @export
 * @param {string} projectId
 * @param {{ name?: string; cover_url?: string; }} payload
 * @return {*}
 */
export async function updateTeamProject(
  projectId: string,
  payload: { name?: string; cover_url?: string; },
) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamProjectUpdate,
      method: 'POST',
      data: {
        project_id: projectId,
        ...payload,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 项目删除（POST /team/project/delete.json）
 *
 * @export
 * @param {string} projectId
 * @return {*}
 */
export async function deleteTeamProject(projectId: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamProjectDelete,
      method: 'POST',
      data: {
        project_id: projectId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/** 为项目副本中的单个源画布创建 Agent 目标房间。 */
async function createTeamDuplicateProjectRoomPayload(
  room: TeamHomeProjectRoomItem,
): Promise<TeamDuplicateProjectRoomPayload> {
  const sourceRoomId = String(room.room_id ?? '').trim();
  if (!sourceRoomId) throw new Error();
  const agentResponse = await createAgentTeamRoom();
  const targetRoomId = String(agentResponse?.data?.result?.room_id ?? '').trim();
  if (agentResponse?.data?.code !== 0 || !targetRoomId) {
    throw new Error(agentResponse?.data?.message || '');
  }
  return {
    room_id: sourceRoomId,
    to_room_id: targetRoomId,
  };
}

/**
 * 团队版 · 项目副本（POST /team/project/duplicate.json）
 *
 * @export
 * @param {string} projectId
 * @param {{ rooms: TeamHomeProjectRoomItem[]; folder_id?: string | number; }} params
 * @return {Promise<TeamDuplicateProjectResult>}
 */
export async function duplicateTeamProject(
  projectId: string,
  params: {
    rooms: TeamHomeProjectRoomItem[];
    folder_id?: string | number;
  },
): Promise<TeamDuplicateProjectResult> {
  if (!params.rooms?.length) throw new Error();
  const rooms = await Promise.all(params.rooms.map(createTeamDuplicateProjectRoomPayload));
  return new Promise<TeamDuplicateProjectResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamProjectDuplicate,
      method: 'POST',
      data: {
        project_id: projectId,
        rooms,
        folder_id: params.folder_id == null ? undefined : Number(params.folder_id),
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data);
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 画布列表（GET /team/canvas/list.json?project_id=）
 *
 * @export
 * @param {string} projectId
 * @return {Promise<V3CanvasListResult>}
 */
export async function getTeamCanvasList(projectId: string) {
  return new Promise<V3CanvasListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamCanvasList,
      method: 'GET',
      params: {
        project_id: projectId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve({
        list: (response.data.data?.list || []) as V3CanvasListItem[],
      });
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 项目内 Agent 房间列表（GET /team/room/list.json）。
 * canvasRequest 会携带当前团队的 Client-OID，不能使用个人版的 skipClientOId。
 */
export async function getTeamRoomList(projectId: string): Promise<V3CanvasRoomListResult> {
  return new Promise<V3CanvasRoomListResult>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamRoomList,
      method: 'GET',
      params: {
        project_id: projectId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve({
        list: (response.data.data?.list || []) as V3CanvasRoomListItem[],
      });
      return;
    }
    reject(response);
  });
}

/** 删除团队项目内的 Agent 房间（POST /team/room/delete.json）。 */
export async function deleteTeamRoom(projectId: string, roomId: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamRoomDelete,
      method: 'POST',
      data: { project_id: projectId, room_id: roomId },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/** 团队版 Agent 房间重命名（POST /team/room/rename.json）。 */
export async function renameTeamRoom(projectId: string, roomId: string, name: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamRoomRename,
      method: 'POST',
      data: {
        project_id: projectId,
        room_id: roomId,
        name,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 将已创建的 Agent 房间登记到团队项目（POST /team/room/create.json）。
 * 房间 ID 由 Agent 创建接口提供，此处只负责项目内绑定。
 */
export async function bindTeamRoomToProject(projectId: string, roomId: string): Promise<void> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamRoomCreate,
    method: 'POST',
    data: {
      project_id: projectId,
      room_id: roomId,
    },
  });
  if (response.status === 200 && response.data.code === 0) return;
  throw new Error(response.data?.message || 'bind team room failed');
}

/**
 * 团队版 · 创建 / 绑定画布（POST /team/canvas/create.json）
 * 网络拒绝直接交给调用方处理，避免创建流程一直等待而无法重试。
 *
 * @export
 * @param {{ project_id?: string; room_id?: string; name?: string; folder_id?: string | number; case_id?: string | number; }} [params]
 * @return {Promise<{ project_id: string; room_id: string; canvas_id: string; name?: string; }>}
 */
export async function createTeamCanvas(params: {
  project_id?: string;
  room_id?: string;
  name?: string;
  folder_id?: string | number;
  case_id?: string | number;
} = {}): Promise<{
    project_id: string;
    room_id: string;
    canvas_id: string;
    name?: string;
  }> {
  const response = await canvasRequest.request({
    url: APP.roboneo.api.teamCanvasCreate,
    method: 'POST',
    data: {
      project_id: params.project_id,
      room_id: params.room_id,
      name: params.name,
      folder_id: params.folder_id == null ? undefined : Number(params.folder_id),
      case_id: params.case_id == null ? undefined : Number(params.case_id),
      alloc_canvas_id: true,
    },
  });
  if (response.status === 200 && response.data.code === 0) {
    const { data } = response.data;
    // room/submit must use the Agent room created before canvas binding, not the canvas response identity.
    const submitRoomId = params.room_id == null ? '' : String(params.room_id).trim();
    if (submitRoomId) {
      await submitAgentTeamRoomOnCreate(submitRoomId);
    }
    return data;
  }
  return Promise.reject(response);
}

/**
 * 按项目内已有画布数量生成下一张画布的默认名称（与 Team Studio 顶栏 CanvasSwitcher 一致）。
 *
 * @export
 * @param {string} projectId
 * @param {string} namePrefix
 * @return {Promise<string>}
 */
export async function resolveNextCanvasName(
  projectId: string,
  namePrefix: string,
): Promise<string> {
  try {
    const isTeamEdition = Boolean(useOfficialStore.getState().clientOId);
    const res = isTeamEdition
      ? await getTeamCanvasList(projectId)
      : await getCanvasListV3(projectId);
    return `${namePrefix}${(res.list || []).length + 1}`;
  } catch {
    return `${namePrefix}1`;
  }
}

/** 在已有项目下独立创建画布，并在前端补齐顺序默认名称。 */
export async function createCanvasInProject(params: {
  project_id: string;
  name?: string;
  /** 未传 name 时按「前缀 + 项目内画布数 + 1」生成默认名称。 */
  namePrefix?: string;
}): Promise<{ project_id: string; canvas_id: string; name?: string; }> {
  let { name } = params;
  if (!name && params.namePrefix) {
    name = await resolveNextCanvasName(params.project_id, params.namePrefix);
  }
  const isTeamEdition = Boolean(useOfficialStore.getState().clientOId);
  const response = await canvasRequest.request({
    url: isTeamEdition ? APP.roboneo.api.teamCanvasCreate : APP.roboneo.api.canvasCreateV3,
    method: 'POST',
    ...(!isTeamEdition ? { skipClientOId: true } : {}),
    data: { project_id: params.project_id, name, alloc_canvas_id: true },
  });
  if (response.status !== 200 || response.data.code !== 0) return Promise.reject(response);
  const created = response.data.data;
  const canvasId = created?.canvas_id == null ? '' : String(created.canvas_id).trim();
  if (!canvasId) throw new Error('create canvas failed: missing canvas_id');
  console.log('[createCanvasInProject] canvas_id:', canvasId);
  return { project_id: params.project_id, canvas_id: canvasId, name: created.name };
}

/**
 * 团队版 · 画布重命名（POST /team/canvas/rename.json）
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} roomId 传 canvas_id 操作独立画布；字符串参数兼容旧 room_id。
 * @param {string} name
 * @return {*}
 */
export async function renameTeamCanvas(projectId: string, roomId: CanvasIdentifier, name: string) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamCanvasRename,
      method: 'POST',
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
        name,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 画布删除（POST /team/canvas/delete.json）
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} [roomId] 传 canvas_id 操作独立画布；字符串参数兼容旧 room_id。
 * @return {*}
 */
export async function deleteTeamCanvas(projectId: string, roomId?: CanvasIdentifier) {
  return new Promise<Record<string, never>>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamCanvasDelete,
      method: 'POST',
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      resolve(response.data.data || {});
      return;
    }
    reject(response);
  });
}

/**
 * 团队版 · 画布副本（POST /team/canvas/duplicate.json）
 *
 * @export
 * @param {string} projectId
 * @param {CanvasIdentifier} [roomId] 源 canvas_id，字符串参数兼容旧 room_id。
 * @param {string} [toRoomId] 前端预生成的新画布 room_id
 * @return {Promise<{ project_id: string; room_id: string; name?: string; }>}
 */
export async function duplicateTeamCanvas(projectId: string, roomId?: CanvasIdentifier, toRoomId?: string) {
  return new Promise<{
    project_id: string;
    room_id: string;
    canvas_id?: string;
    name?: string;
  }>(async (resolve, reject) => {
    const response = await canvasRequest.request({
      url: APP.roboneo.api.teamCanvasDuplicate,
      method: 'POST',
      data: {
        project_id: projectId,
        ...(typeof roomId === 'string' ? { room_id: roomId } : roomId),
        to_room_id: toRoomId,
      },
    });
    if (response.status === 200 && response.data.code === 0) {
      const { data } = response.data;
      const targetRoomId = data?.room_id ? String(data.room_id) : toRoomId;
      if (typeof roomId === 'string' && targetRoomId) {
        await submitAgentTeamRoomOnCreate(targetRoomId);
      }
      resolve(data);
      return;
    }
    reject(response);
  });
}
